import { normalizeBearingDeg } from "../utils/bearing-degrees.js";
//#region ../src/geo/solar-position.ts
/**
* The real sun for a place and an instant (plan 2026-09-23-2149, M1;
* DEC-SUN-2): the NOAA solar-calculator algorithm (Meeus, "Astronomical
* Algorithms", ch. 25, low-precision form). Measured within 0.019° of a
* VSOP87 reference (astronomy-engine) over 1800-2100; the sun's disc is
* 0.53° wide. A few dozen trigonometric operations, no dependency.
*
* GEOMETRIC positions: no atmospheric refraction (the sky model is
* geometric). An augmented-reality overlay on the REAL sun would add
* refraction (about half a degree near the horizon) on top.
*
* Conventions: elevation above the horizon; azimuth clockwise from north
* (east = 90°), the frame `sunDirection` in OsmDemo expects. Instants are
* epoch milliseconds (UTC); a "solar date" is the local APPARENT solar day
* at a longitude, centred on its solar noon, so day boundaries never depend
* on the browser's time zone.
*
* @see solar-position.ts.md
*/
const DEG = Math.PI / 180;
const DAY_MS = 864e5;
function requireFinite(name, value) {
	if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite, got ${value}`);
}
function requirePlace(latDeg, lngDeg) {
	requireFinite("latitude", latDeg);
	requireFinite("longitude", lngDeg);
	if (Math.abs(latDeg) > 90) throw new RangeError(`latitude must be within ±90°, got ${latDeg}`);
}
/** Declination (rad) and the equation of time (minutes) at an instant. */
function sunAt(ms) {
	const t = (ms / DAY_MS + 2440587.5 - 2451545) / 36525;
	const l0 = normalizeBearingDeg(280.46646 + t * (36000.76983 + t * 3032e-7));
	const m = 357.52911 + t * (35999.05029 - 1537e-7 * t);
	const e = .016708634 - t * (42037e-9 + 1.267e-7 * t);
	const mr = m * DEG;
	const c = Math.sin(mr) * (1.914602 - t * (.004817 + 14e-6 * t)) + Math.sin(2 * mr) * (.019993 - 101e-6 * t) + Math.sin(3 * mr) * 289e-6;
	const omega = (125.04 - 1934.136 * t) * DEG;
	const apparentLongitude = (l0 + c - .00569 - .00478 * Math.sin(omega)) * DEG;
	const obliquity = (23 + (26 + (21.448 - t * (46.815 + t * (59e-5 - t * .001813))) / 60) / 60 + .00256 * Math.cos(omega)) * DEG;
	const declination = Math.asin(Math.sin(obliquity) * Math.sin(apparentLongitude));
	const y = Math.tan(obliquity / 2) ** 2;
	const l0r = l0 * DEG;
	return {
		declination,
		eotMinutes: 4 * (y * Math.sin(2 * l0r) - 2 * e * Math.sin(mr) + 4 * e * y * Math.sin(mr) * Math.cos(2 * l0r) - .5 * y * y * Math.sin(4 * l0r) - 1.25 * e * e * Math.sin(2 * mr)) / DEG
	};
}
/** Local apparent solar time at a longitude, hours in [0, 24). */
function apparentSolarTimeHours(ms, lngDeg) {
	requireFinite("instant", ms);
	requireFinite("longitude", lngDeg);
	const epoch = apparentEpoch(ms, lngDeg);
	const hours = (epoch - Math.floor(Math.floor(epoch) / DAY_MS) * DAY_MS) / 36e5;
	return hours >= 24 ? 0 : hours;
}
/** Validated air conditions, with the standard defaults filled in. */
function airOf(conditions) {
	const pressure = conditions.pressureHPa ?? 1010;
	const temperature = conditions.temperatureC ?? 10;
	if (!(Number.isFinite(pressure) && pressure >= 0)) throw new RangeError(`pressure must be a finite number ≥ 0 hPa, got ${pressure}`);
	if (!(Number.isFinite(temperature) && temperature > -273)) throw new RangeError(`temperature must be above −273 °C, got ${temperature} °C`);
	return {
		pressure,
		temperature
	};
}
/**
* How far the air lifts a body at a GEOMETRIC elevation, degrees: the
* APPARENT elevation is the geometric one plus this. Saemundsson's formula
* (Meeus, "Astronomical Algorithms", ch. 16: the true-to-apparent direction;
* Bennett's formula is its inverse), with astronomy-engine's 'normal'
* convention below the horizon, where the formula diverges near −5°: clamped
* at −1°, then tapered linearly to zero at the nadir (JPL Horizons only
* clamps; the taper is astronomy-engine's). Scaled by pressure / 1010 hPa and
* 283 K / temperature (Meeus 16.4).
*
* 0.48° for a sun geometrically ON the horizon, 0.57° for one geometrically
* 0.57° below it (which therefore appears exactly on it), ~1′ at 45°, 0 at
* the zenith. For drawing on the REAL sky (an AR
* sun icon); the sky model itself is geometric.
*/
function atmosphericRefractionDeg(geometricElevationDeg, conditions = {}) {
	requireFinite("elevation", geometricElevationDeg);
	const { pressure, temperature } = airOf(conditions);
	const h = geometricElevationDeg;
	if (h < -90 || h > 90) return 0;
	const clamped = Math.max(h, -1);
	let arcMinutes = 1.02 / Math.tan((clamped + 10.3 / (clamped + 5.11)) * DEG);
	if (h < -1) arcMinutes *= (h + 90) / 89;
	return arcMinutes / 60 * (pressure / 1010) * (283 / (273 + temperature));
}
/**
* The sun's elevation and azimuth at an instant and a place: GEOMETRIC by
* default, APPARENT (refracted) with `{ refraction }`. Refraction only lifts
* the elevation; the azimuth is unchanged.
*/
function solarPosition(ms, latDeg, lngDeg, options = {}) {
	requireFinite("instant", ms);
	requirePlace(latDeg, lngDeg);
	const { declination } = sunAt(ms);
	const hourAngle = (apparentSolarTimeHours(ms, lngDeg) * 15 - 180) * DEG;
	const lat = latDeg * DEG;
	const cosZenith = Math.sin(lat) * Math.sin(declination) + Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngle);
	const zenith = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
	const azimuth = Math.atan2(Math.sin(hourAngle), Math.cos(hourAngle) * Math.sin(lat) - Math.tan(declination) * Math.cos(lat)) + Math.PI;
	const geometric = Math.PI / 2 - zenith;
	const refraction = options.refraction ?? false;
	return {
		elevationRad: geometric + (refraction === false ? 0 : atmosphericRefractionDeg(geometric / DEG, refraction === true ? {} : refraction) * DEG),
		azimuthRad: (azimuth % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI)
	};
}
/**
* 00:00 UTC of a calendar date, in ms. Built with setUTCFullYear, so years
* 0-99 stay real years (Date.UTC maps them to 1900-1999), and checked by a
* round trip, so 31 Feb or month 13 is rejected instead of rolling over
* (M1 review, finding 4).
*/
function utcStartOf(date) {
	const { year, month, day } = date;
	const d = /* @__PURE__ */ new Date(0);
	d.setUTCFullYear(year, month - 1, day);
	const ms = d.getTime();
	if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || !Number.isFinite(ms) || d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) throw new RangeError(`not a calendar date: ${year}-${month}-${day}`);
	return ms;
}
/** The local apparent solar clock at an instant, as a pseudo-UTC epoch. */
const apparentEpoch = (ms, lngDeg) => ms + lngDeg * 24e4 + sunAt(ms).eotMinutes * 6e4;
/**
* The solar date an instant falls in at a longitude: the calendar date of
* the local apparent solar clock, so the date changes exactly where
* apparent solar time wraps through 00:00 (M1 review, finding 1).
*/
function solarDateAt(ms, lngDeg) {
	requireFinite("instant", ms);
	requireFinite("longitude", lngDeg);
	const d = new Date(Math.floor(apparentEpoch(ms, lngDeg)));
	return {
		year: d.getUTCFullYear(),
		month: d.getUTCMonth() + 1,
		day: d.getUTCDate()
	};
}
/** The instant the apparent solar clock reads `offsetMs` into a date. */
function apparentInstant(date, lngDeg, offsetMs) {
	requireFinite("longitude", lngDeg);
	let ms = utcStartOf(date) + offsetMs - lngDeg * 24e4;
	const mean = ms;
	for (let pass = 0; pass < 3; pass++) ms = mean - sunAt(ms).eotMinutes * 6e4;
	return ms;
}
/**
* The instant the local apparent solar clock reads `hours` on a solar date
* (the inverse of `apparentSolarTimeHours` within that date). RangeError
* for hours outside [0, 24).
*/
function instantAtApparentSolarTime(date, lngDeg, hours) {
	requireFinite("hours", hours);
	if (hours < 0 || hours >= 24) throw new RangeError(`hours must be in [0, 24), got ${hours}`);
	return Math.max(solarMidnight(date, lngDeg), apparentInstant(date, lngDeg, hours * 36e5));
}
/** The instant of solar noon (the sun on the meridian) on a solar date. */
function solarNoon(date, lngDeg) {
	return apparentInstant(date, lngDeg, DAY_MS / 2);
}
const sameDate = (a, b) => a.year === b.year && a.month === b.month && a.day === b.day;
/**
* The first millisecond of a solar date: apparent solar midnight, where
* `solarDateAt` changes to this date. Consecutive dates TILE: one date ends
* exactly where the next starts (the first version used noon − 12 h, which
* left seams of up to 30 s; M1 review, finding 1).
*/
function solarMidnight(date, lngDeg) {
	let ms = Math.ceil(apparentInstant(date, lngDeg, 0));
	while (!sameDate(solarDateAt(ms, lngDeg), date)) ms += 1;
	while (sameDate(solarDateAt(ms - 1, lngDeg), date)) ms -= 1;
	return ms;
}
/** The calendar date after `date`. */
function nextDate(date) {
	const d = new Date(utcStartOf(date) + DAY_MS);
	return {
		year: d.getUTCFullYear(),
		month: d.getUTCMonth() + 1,
		day: d.getUTCDate()
	};
}
const elevationDegAt = (ms, lat, lng) => solarPosition(ms, lat, lng).elevationRad / DEG;
/**
* The instant on a solar date when the sun crosses `elevationDeg`, on its
* rising limb (solar midnight → noon) or its setting limb (noon → the next
* midnight). `null` when it never does that day (midnight sun, polar night,
* white nights below civil dusk). Bisection to 1 ms.
*
* Bisection assumes the elevation is monotone on each limb. It is NEARLY
* so: the declination drifts during the day, and the measured excursions
* are ≤ 2e-4° below 80° of latitude and ~2e-3° near the pole (M1 review,
* finding 2). An elevation inside such an excursion can return `null`;
* irrelevant for the thresholds the demos use (−6°, 3.5°) below ~85°.
*/
function timeAtElevation(date, latDeg, lngDeg, elevationDeg, limb) {
	requirePlace(latDeg, lngDeg);
	requireFinite("elevation", elevationDeg);
	const noon = solarNoon(date, lngDeg);
	let low = limb === "rising" ? solarMidnight(date, lngDeg) : noon;
	let high = limb === "rising" ? noon : solarMidnight(nextDate(date), lngDeg);
	const above = (ms) => elevationDegAt(ms, latDeg, lngDeg) >= elevationDeg;
	const startAbove = above(low);
	const endAbove = above(high);
	if (limb === "rising" ? startAbove || !endAbove : !startAbove || endAbove) return null;
	while (high - low > 1) {
		const mid = (low + high) / 2;
		if (above(mid) === endAbove) high = mid;
		else low = mid;
	}
	return high;
}
//#endregion
export { apparentSolarTimeHours, atmosphericRefractionDeg, instantAtApparentSolarTime, solarDateAt, solarMidnight, solarNoon, solarPosition, timeAtElevation };
